import React, { useEffect, useRef } from 'react';
import elfsightMapping from './elfsightMapping.json';

type ElfsightMapping = Record<string, string>;

interface IProps {
  uuid: string;
}

declare global {
  interface Window {
    eapps?: {
      Platform?: {
        initWidgetIfVisible?: (element: HTMLElement) => void;
        initWidget?: (element: HTMLElement) => void;
      };
    };
  }
}

const ELFSIGHT_SCRIPT_URL = 'https://elfsightcdn.com/platform.js';

const instagramMap = elfsightMapping as ElfsightMapping;

// Cache for the loading promise
let loadingPromise: Promise<void> | null = null;

/**
 * Waits for window.eapps.Platform to become available.
 * Uses polling with a timeout to handle edge cases where load event already fired.
 */
const waitForPlatform = (timeoutMs = 5000): Promise<void> => new Promise((resolve, reject) => {
  const startTime = Date.now();

  const check = () => {
    if (window.eapps?.Platform) {
      resolve();
      return;
    }

    if (Date.now() - startTime > timeoutMs) {
      reject(new Error('Elfsight Platform initialization timeout'));
      return;
    }

    setTimeout(check, 50);
  };

  check();
});

/**
 * Loads the Elfsight platform script dynamically.
 * Returns a promise that resolves when the script is loaded.
 * Uses promise caching to prevent duplicate script loading.
 */
const loadElfsightScript = (): Promise<void> => {
  if (loadingPromise) {
    return loadingPromise;
  }

  if (window.eapps?.Platform) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector(`script[src="${ELFSIGHT_SCRIPT_URL}"]`);

  if (existingScript) {
    // Script exists - wait for Platform to be available
    loadingPromise = waitForPlatform();
    return loadingPromise;
  }

  // Create and load new script
  loadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = ELFSIGHT_SCRIPT_URL;
    script.async = true;

    script.onload = () => {
      waitForPlatform()
        .then(resolve)
        .catch(reject);
    };

    script.onerror = () => reject(new Error('Failed to load Elfsight script'));

    document.head.appendChild(script);
  });

  return loadingPromise;
};

/**
 * Initializes Elfsight widget for a specific container element.
 * Uses Elfsight's internal API to reinitialize widgets on SPA navigation.
 */
const initializeWidget = (container: HTMLElement | null): void => {
  if (!container) return;

  const platform = window.eapps?.Platform;

  if (platform?.initWidgetIfVisible) {
    // Preferred method - initializes widget if visible in viewport
    platform.initWidgetIfVisible(container);
  } else if (platform?.initWidget) {
    // Fallback method
    platform.initWidget(container);
  }
};

const ElfsightInstagramWidget = ({ uuid }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const className = instagramMap?.[String(uuid)];

  useEffect(() => {
    if (!className) return undefined;

    let isMounted = true;

    const initWidget = async () => {
      try {
        await loadElfsightScript();

        if (isMounted && containerRef.current) {
          initializeWidget(containerRef.current);
        }
      } catch (error) {
        console.error('ElfsightInstagramWidget: Failed to initialize');
      }
    };

    initWidget().catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [className, uuid]);

  if (!className) {
    return null;
  }

  return (
    <div
      key={className}
      ref={containerRef}
      className={className}
      data-elfsight-app-lazy
    />
  );
};

export default ElfsightInstagramWidget;
